import { MissionTimeSlot } from '../../../interfaces/missions/MissionTimeSlot';
import { IDisplayedWorkWindow, IWorkWindow } from '../../../interfaces/missions/IMissionWorkWindows';
import { format } from 'date-fns';
import { timeToDate } from '../../utils/format.utils';

/**
 * Crée une liste de WorkWindows à partir d'un TimeSlot
 * @param timeSlot
 */
const createWorkWindows = (timeSlot: MissionTimeSlot, batchId: number): IWorkWindow[] => {
    const windows: IWorkWindow[] = [];
    //sort dates chronologically
    timeSlot.dates.sort((a, b) => {
        const dateA = new Date(a);
        const dateB = new Date(b);
        return dateA.getTime() - dateB.getTime();
    });
    timeSlot.dates.forEach((date) => {
        const startTime = timeToDate(date, timeSlot.start_time);
        if (timeSlot.has_break) {
            const preBreakInterval = timeToDate(timeSlot.start_time, timeSlot.after_break_start_time, startTime);
            const breakInterval = timeToDate(
                timeSlot.after_break_start_time,
                timeSlot.after_break_end_time,
                preBreakInterval
            );
            const postBreakInterval = timeToDate(timeSlot.after_break_end_time, timeSlot.end_time, breakInterval);
            windows.push(
                {
                    startTime: startTime,
                    endTime: preBreakInterval,
                    isBreak: false,
                    batchId: batchId,
                },
                {
                    startTime: preBreakInterval,
                    endTime: breakInterval,
                    isBreak: true,
                    batchId: batchId,
                },
                {
                    startTime: breakInterval,
                    endTime: postBreakInterval,
                    isBreak: false,
                    batchId: batchId,
                }
            );
        } else {
            windows.push({
                startTime: startTime,
                endTime: timeToDate(timeSlot.start_time, timeSlot.end_time, startTime),
                isBreak: false,
                batchId: batchId,
            });
        }
    });
    return windows;
};

const getDisplayableWorkWindows = (workWindows: IWorkWindow[]): IDisplayedWorkWindow[] => {
    const displayable: IDisplayedWorkWindow[] = [];
    for (let i = 0; i < workWindows.length; i++) {
        if (workWindows[i].isBreak === true) {
            continue;
        }
        if (workWindows[i + 1]?.isBreak === true && i + 2 < workWindows.length) {
            displayable.push({
                batchId: workWindows[i].batchId,
                schedule: [
                    { startTime: workWindows[i].startTime, endTime: workWindows[i].endTime },
                    { startTime: workWindows[i + 2].startTime, endTime: workWindows[i + 2].endTime },
                ],
            });
            i += 2;
        } else {
            displayable.push({
                batchId: workWindows[i].batchId,
                schedule: [{ startTime: workWindows[i].startTime, endTime: workWindows[i].endTime }],
            });
        }
    }
    return displayable.sort(
        (a: IDisplayedWorkWindow, b: IDisplayedWorkWindow) =>
            a.schedule[0]?.startTime?.getTime() - b.schedule[0]?.startTime?.getTime()
    );
};

const createTimeSlotsFromWorkWindows = (workWindows: IDisplayedWorkWindow[]): MissionTimeSlot[] => {
    return workWindows.map((workWindow) => {
        const timeSlot = new MissionTimeSlot();
        timeSlot.dates = [];
        timeSlot.start_time = workWindow.schedule[0].startTime;
        timeSlot.end_time = workWindow.schedule[0].endTime;
        if (workWindow.schedule.length > 1) {
            timeSlot.has_break = true;
            timeSlot.after_break_start_time = workWindow.schedule[0].endTime;
            timeSlot.after_break_end_time = workWindow.schedule[1].startTime;
            timeSlot.end_time = workWindow.schedule[1].endTime;
        }
        return timeSlot;
    });
};

const encodeWorkWindows = (workWindows: IWorkWindow[]): any[] => {
    return workWindows.map((workWindow) => {
        return {
            ...workWindow,
            startTime: format(workWindow.startTime, 'yyyy-MM-dd HH:mm'),
            endTime: format(workWindow.endTime, 'yyyy-MM-dd HH:mm'),
            isBreak: +workWindow.isBreak,
        };
    });
};

const filterWorkWindow = (workWindows: IWorkWindow[]): IDisplayedWorkWindow[] => {
    const windows = getDisplayableWorkWindows(workWindows)
        .filter((workWindow, index, arr) => arr.findIndex((value) => value.batchId === workWindow.batchId) === index)
        .sort((a, b) => a.batchId - b.batchId);
    const today = new Date().toISOString().split('T')[0]; // Récupère la date actuelle au format YYYY-MM-DD

    windows.forEach((item) => {
        item.schedule.forEach((slot) => {
            const startTime = new Date(slot.startTime);
            const endTime = new Date(slot.endTime);

            // Mettre à jour les heures avec la date actuelle
            slot.startTime = new Date(`${today}T${startTime.toTimeString().split(' ')[0]}`);
            slot.endTime = new Date(`${today}T${endTime.toTimeString().split(' ')[0]}`);
        });
    });

    return windows;
};

export type WorkWindowReturn = {
    createWorkWindows: (timeSlot: MissionTimeSlot, batchId: number) => IWorkWindow[];
    getDisplayableWorkWindows: (workWindows: IWorkWindow[]) => IDisplayedWorkWindow[];
    createTimeSlotsFromWorkWindows: (workWindows: IDisplayedWorkWindow[]) => MissionTimeSlot[];
    encodeWorkWindows: (workWindows: IWorkWindow[]) => any[];
    filterWorkWindow: (workWindows: IWorkWindow[]) => IDisplayedWorkWindow[];
};

export const workWindow = (): WorkWindowReturn => {
    return {
        createWorkWindows,
        getDisplayableWorkWindows,
        createTimeSlotsFromWorkWindows,
        encodeWorkWindows,
        filterWorkWindow,
    };
};
